import React, { useMemo, useRef, useCallback, useState } from 'react';

import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useOutsideHandler } from 'src/common/hooks';

import { fetchSalesforceAccounts } from '../api';
import { SalesforceAccountType } from '../types';

const SearchSalesForceAccounts: React.FC = () => {
  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const [searchError, setSearchError] = useState('');
  const [filteredAccounts, setFilteredAccounts] = useState<SalesforceAccountType[]>([]);
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { setValue } = useFormContext();

  const fetch: (value: string) => void = async (value) => {
    try {
      const response = await fetchSalesforceAccounts(value);
      setFilteredAccounts(response.data);
    } catch (err) {
      const error = err as AxiosError<{ detail: string }>;
      const detail = error?.response?.data?.detail;
      setSearchError(detail || 'An unknown error has occurred');
      setFilteredAccounts([]);
    }
  };

  const debounceHandler = useMemo(() => debounce(fetch, 1000), []);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setSearchError('');
      const text = event.target.value;
      if (!text) {
        setFilteredAccounts([]);
        return;
      }
      debounceHandler(text);
    },
    [debounceHandler]
  );

  const handleOnBlur = (): void => {
    setFilteredAccounts([]);
  };

  const handleSetAccount = (account: SalesforceAccountType) => () => {
    setFilteredAccounts([]);
    setValue('salesforce_id', account.id, { shouldDirty: true });
  };

  useOutsideHandler(menuRef, handleOnBlur);

  return (
    <div className="relative" ref={menuRef}>
      <label htmlFor="search-salesforce" className="input-label">
        Search for account in Salesforce
        <input
          name="search-salesforce"
          ref={searchRef}
          className="input w-full mt-1 transition duration-150 ease-in-out"
          placeholder="Search by name"
          data-testid="salesforce-accounts-search"
          onChange={handleOnChange}
          type="text"
          autoComplete="off"
        />
        {searchError && <p className="error-text">{searchError}</p>}
      </label>
      <div
        className="absolute left-0 bg-white-0.0 min-w-96 mt-2 origin-top-right rounded shadow-lg z-50 border border-white-1.0 max-h-80 overflow-auto"
        hidden={filteredAccounts.length === 0}
      >
        {filteredAccounts.map((account) => (
          <div
            key={account.id}
            className="p-4 border-b border-white-1.0 hover:bg-white-1.0 cursor-pointer"
            role="presentation"
            onClick={handleSetAccount(account)}
            data-testid={`sf-account-${account.id}`}
          >
            {account.name}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SearchSalesForceAccounts;
